import { Trash2 } from 'lucide-react';
import type { ComponentProps } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { cn } from '@/lib/utils';

type DeleteButtonProps = Omit<ComponentProps<typeof Button>, 'variant' | 'children'> & {
	/** Shows a spinner instead of the icon and disables the button */
	pending?: boolean;
	/** Only the icon; the label becomes its accessible name and tooltip */
	iconOnly?: boolean;
	/** Defaults to "Delete" */
	label?: string;
};

/** The one delete button of the app: solid red with a trash icon, or a red icon-only button in tables. */
export function DeleteButton({
	pending = false,
	iconOnly = false,
	label,
	size,
	className,
	disabled,
	...props
}: DeleteButtonProps) {
	const { t } = useTranslation();
	const text = label ?? t('common.actions.delete');
	const icon = pending ? <Spinner /> : <Trash2 />;

	if (iconOnly) {
		return (
			<Button
				type="button"
				variant="ghost"
				size={size ?? 'icon-sm'}
				className={cn('text-destructive hover:bg-destructive/10 hover:text-destructive', className)}
				aria-label={text}
				title={text}
				disabled={disabled || pending}
				{...props}
			>
				{icon}
			</Button>
		);
	}
	return (
		<Button
			type="button"
			variant="destructive"
			size={size ?? 'sm'}
			className={className}
			disabled={disabled || pending}
			{...props}
		>
			{icon}
			{text}
		</Button>
	);
}
